import {
  COUNTY_LABELS,
  PARTNER_CATEGORIES,
  countyLabel,
  formatE164,
  partnerCategoryLabel,
  partnerDisplayName,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { navigate, useAsync } from "../../lib/hooks";
import {
  fetchPartners,
  fetchReferrals,
  type Partner,
} from "../../lib/partnerQueries";
import { useSession } from "../../session";

/**
 * The drawer, made searchable.
 *
 * Sorted by "who have I not spoken to in longest", because that is the question
 * the screen is for. A list sorted by date added answers "who did I meet
 * recently", which nobody needs — the recent ones are the ones you remember.
 */

type SortKey = "quietest" | "referrals" | "newest";

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / 86_400_000);
}

function lastTextedWords(partner: Partner): string {
  const days = daysSince(partner.last_texted_at);
  if (days === null) return "Never";
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 60) return `${days} days ago`;
  return `${Math.round(days / 30)} months ago`;
}

export function PartnersList({ search }: { search: string }) {
  const { people, profileId } = useSession();
  const partnersQuery = useAsync(fetchPartners, []);
  const referralsQuery = useAsync(() => fetchReferrals(), []);

  const [category, setCategory] = useState("");
  const [owner, setOwner] = useState("");
  const [county, setCounty] = useState("");
  const [consent, setConsent] = useState("");
  const [sort, setSort] = useState<SortKey>("quietest");

  const referralCounts = useMemo(() => {
    const counts = new Map<string, { from: number; to: number }>();
    for (const row of referralsQuery.data ?? []) {
      const current = counts.get(row.partner_id) ?? { from: 0, to: 0 };
      if (row.direction === "from_partner") current.from += 1;
      else current.to += 1;
      counts.set(row.partner_id, current);
    }
    return counts;
  }, [referralsQuery.data]);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = (partner: Partner) => {
      if (!query) return true;
      return [
        partner.first_name,
        partner.last_name,
        partner.company,
        partner.title,
        partner.met_at,
        partner.city,
        partner.email,
        formatE164(partner.phone_mobile),
        partner.phone_mobile,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(query));
    };

    const filtered = (partnersQuery.data ?? []).filter(
      (partner) =>
        matches(partner) &&
        (!category || partner.category === category) &&
        (!owner || partner.owner_profile_id === owner) &&
        (!county || partner.county === county) &&
        (!consent || partner.sms_status === consent),
    );

    const referrals = (partner: Partner) => {
      const counts = referralCounts.get(partner.id);
      return (counts?.from ?? 0) * 10 + (counts?.to ?? 0);
    };

    return [...filtered].sort((a, b) => {
      if (sort === "referrals") return referrals(b) - referrals(a);
      if (sort === "newest") return b.created_at.localeCompare(a.created_at);
      // Quietest first, and never-texted before everybody — a partner nobody
      // has ever contacted is the one this screen exists to surface.
      const aTexted = a.last_texted_at ?? "";
      const bTexted = b.last_texted_at ?? "";
      return aTexted.localeCompare(bTexted);
    });
  }, [
    partnersQuery.data,
    referralCounts,
    search,
    category,
    owner,
    county,
    consent,
    sort,
  ]);

  const needAsking = (partnersQuery.data ?? []).filter(
    (partner) => partner.sms_status === "no_consent",
  ).length;

  if (partnersQuery.error) {
    return (
      <div className="list">
        <div className="empty">
          <b>Could not load partners</b>
          {partnersQuery.error}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="listhead">
        <h2>Partners</h2>
        <div className="lh-right">
          <span className="count">
            {rows.length} {rows.length === 1 ? "partner" : "partners"}
          </span>
          <button
            className="btn-primary"
            onClick={() => navigate("partner=new")}
          >
            + Scan a card
          </button>
        </div>
      </div>

      <div className="scoreboard">
        <div className="sb-fig">
          <span className="k">In the drawer</span>
          <b>{partnersQuery.data?.length ?? 0}</b>
        </div>
        <div className="sb-fig">
          <span className="k">Referrals from them</span>
          <b>
            {
              (referralsQuery.data ?? []).filter(
                (row) => row.direction === "from_partner",
              ).length
            }
          </b>
        </div>
        <div className="sb-fig">
          <span className="k">Referrals to them</span>
          <b>
            {
              (referralsQuery.data ?? []).filter(
                (row) => row.direction === "to_partner",
              ).length
            }
          </b>
        </div>
        {/* The one figure that is a job to do rather than a fact. Nobody can be
            texted until somebody has asked them in person, so this is the
            number that decides whether the campaign reaches anybody at all. */}
        <div className={`sb-fig flag${needAsking === 0 ? " quiet" : ""}`}>
          <span className="k">Still to ask</span>
          <b>{needAsking}</b>
        </div>
      </div>

      <div className="partner-filters">
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Every category</option>
          {PARTNER_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {partnerCategoryLabel(value)}
            </option>
          ))}
        </select>

        <select value={owner} onChange={(e) => setOwner(e.target.value)}>
          <option value="">Everybody's</option>
          {profileId && <option value={profileId}>Mine</option>}
          {people
            .filter((person) => person.active !== false)
            .map((person) => (
              <option key={person.id} value={person.id}>
                {person.full_name}
              </option>
            ))}
        </select>

        <select value={county} onChange={(e) => setCounty(e.target.value)}>
          <option value="">Every county</option>
          {Object.entries(COUNTY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <select value={consent} onChange={(e) => setConsent(e.target.value)}>
          <option value="">Any consent</option>
          <option value="opted_in">Can be texted</option>
          <option value="no_consent">Not asked yet</option>
          <option value="opted_out">Opted out</option>
        </select>

        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
        >
          <option value="quietest">Quietest first</option>
          <option value="referrals">Most referrals</option>
          <option value="newest">Newest</option>
        </select>
      </div>

      <div className="list partners">
        <div className="lrow head">
          <div>Name</div>
          <div>Company</div>
          <div>Category</div>
          <div>County</div>
          <div>Last text</div>
          <div>Referrals</div>
          <div>Owner</div>
        </div>

        {rows.length === 0 ? (
          <div className="empty">
            <b>Nobody here yet</b>
            Scan a business card and it lands in this list.
          </div>
        ) : (
          rows.map((partner) => {
            const counts = referralCounts.get(partner.id);
            return (
              <div
                key={partner.id}
                className="lrow"
                onClick={() => navigate(`partner=${partner.id}`)}
              >
                <div>
                  <div className="cust">{partnerDisplayName(partner)}</div>
                  <div className="sub">
                    {formatE164(partner.phone_mobile) ?? "no mobile"}
                  </div>
                </div>
                <div>
                  <div>{partner.company ?? "—"}</div>
                  <div className="sub">
                    {partner.title ?? partner.met_at ?? ""}
                  </div>
                </div>
                <div>
                  <span className="pill">
                    {partnerCategoryLabel(partner.category)}
                  </span>
                </div>
                <div className="sub">{countyLabel(partner.county)}</div>
                <div className="sub">{lastTextedWords(partner)}</div>
                <div className="sub">
                  {counts ? `${counts.from} in / ${counts.to} out` : "—"}
                </div>
                <div>
                  {partner.sms_status === "no_consent" && (
                    <span
                      className="pill ask"
                      title="Ask them next time you see them"
                    >
                      Ask them
                    </span>
                  )}
                  {partner.sms_status === "opted_out" && (
                    <span className="pill out">Opted out</span>
                  )}
                  {partner.sms_status === "opted_in" && (
                    <span className="sub">
                      {partner.owner?.full_name ?? "Unowned"}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
